import React, { useEffect, useState } from "react";
import {
  FaTicketAlt,
  FaPlus,
  FaEye,
  FaTimes,
  FaPaperPlane,
  FaClock,
  FaCheckCircle,
  FaSpinner,
} from "react-icons/fa";
import api from "../../api/axiosClient";

// The backend stores tickets as { type, subject, message, status, priority }.
// The form's category and priority are written as a first line of the message
// (so admins see them too) and read back out here.
const META_RE = /^Category: (.+?) \| Priority: (.+?)\n\n/;

const normalizeTicket = (t) => {
  const raw = t.message || t.description || "";
  const meta = raw.match(META_RE);
  const status = String(t.status || "Open");
  return {
    ...t,
    description: meta ? raw.slice(meta[0].length) : raw,
    category: t.category || (meta ? meta[1] : t.type) || "Other",
    priority: t.priority || (meta ? meta[2] : "Medium"),
    status: status.toLowerCase() === "in review" ? "In Progress" : status,
  };
};

const SupportTicketsTab = ({ addToast }) => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    subject: "",
    category: "Technical Issue",
    priority: "Medium",
    description: "",
  });

  const categories = [
    "Account",
    "Jobs",
    "Payments",
    "Clients",
    "Reviews",
    "Technical Issue",
    "Other",
  ];

  const priorities = ["Low", "Medium", "High"];

  const fetchTickets = async () => {
    try {
      setLoading(true);

      const response = await api.get("/my-tickets/");

      const list = Array.isArray(response.data)
        ? response.data
        : response.data.results || [];
      setTickets(list.map(normalizeTicket));
    } catch (error) {
      console.error("Failed to load support tickets:", error);

      if (addToast) {
        addToast("Unable to load your support tickets.", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const resetForm = () => {
    setForm({
      subject: "",
      category: "Technical Issue",
      priority: "Medium",
      description: "",
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!form.subject.trim()) {
      addToast?.("Please enter a ticket subject.", "error");
      return;
    }

    if (!form.description.trim()) {
      addToast?.("Please describe the issue.", "error");
      return;
    }

    try {
      setSubmitting(true);

      await api.post("/raise-ticket/", {
        subject: form.subject.trim(),
        message:
          `Category: ${form.category} | Priority: ${form.priority}\n\n` +
          form.description.trim(),
      });

      addToast?.("Support ticket created successfully.", "success");

      resetForm();
      setShowForm(false);

      await fetchTickets();
    } catch (error) {
      console.error("Failed to create support ticket:", error);

      const d = error?.response?.data;
      const first =
        d && typeof d === "object" ? Object.values(d)[0] : null;
      const message =
        d?.detail ||
        (Array.isArray(first) ? first[0] : first) ||
        "Unable to create your support ticket.";

      addToast?.(message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusClass = (status) => {
    const normalized = String(status || "").toLowerCase();

    if (normalized === "open") return "status-open";
    if (normalized === "in progress") return "status-progress";
    if (normalized === "resolved") return "status-resolved";
    if (normalized === "closed") return "status-closed";

    return "status-default";
  };

  const formatDate = (date) => {
    if (!date) return "—";

    return new Date(date).toLocaleDateString("en-KE", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div className="support-tickets-tab">
      <style>{`
        .support-tickets-tab {
          font-family: "Raleway", sans-serif;
          background: #ffffff;
          color: #000000;
          padding: 24px;
          min-height: 100%;
        }

        .tickets-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 20px;
          margin-bottom: 28px;
          flex-wrap: wrap;
        }

        .tickets-heading {
          display: flex;
          align-items: center;
          gap: 14px;
        }

        .tickets-heading-icon {
          width: 48px;
          height: 48px;
          background: #fff000;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
        }

        .tickets-heading h2 {
          margin: 0;
          font-size: 26px;
          font-weight: 800;
        }

        .tickets-heading p {
          margin: 5px 0 0;
          color: #666666;
          font-size: 13px;
        }

        .create-ticket-button {
          border: none;
          outline: none;
          box-shadow: none;
          background: #fff000;
          color: #000000;
          border-radius: 9px;
          padding: 11px 18px;
          font-family: inherit;
          font-weight: 800;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .create-ticket-button:hover {
          background: #000000;
          color: #fff000;
        }

        .create-ticket-button:focus,
        .create-ticket-button:active {
          border: none;
          outline: none;
          box-shadow: none;
        }

        .ticket-summary {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 15px;
          margin-bottom: 25px;
        }

        .summary-card {
          border: 1px solid #e5e5e5;
          border-radius: 12px;
          padding: 18px;
          background: #ffffff;
        }

        .summary-card-icon {
          margin-bottom: 10px;
          font-size: 18px;
        }

        .summary-card-value {
          font-size: 23px;
          font-weight: 800;
        }

        .summary-card-label {
          color: #666666;
          font-size: 12px;
          margin-top: 3px;
        }

        .tickets-section {
          border: 1px solid #e5e5e5;
          border-radius: 14px;
          overflow: hidden;
          background: #ffffff;
        }

        .section-header {
          padding: 18px 20px;
          border-bottom: 1px solid #e5e5e5;
        }

        .section-header h3 {
          margin: 0;
          font-size: 18px;
          font-weight: 800;
        }

        .section-header p {
          margin: 5px 0 0;
          color: #666666;
          font-size: 13px;
        }

        .tickets-table-wrapper {
          overflow-x: auto;
        }

        .tickets-table {
          width: 100%;
          border-collapse: collapse;
        }

        .tickets-table th {
          background: #000000;
          color: #ffffff;
          padding: 13px 16px;
          text-align: left;
          font-size: 12px;
          font-weight: 700;
        }

        .tickets-table td {
          padding: 15px 16px;
          border-bottom: 1px solid #eeeeee;
          font-size: 13px;
        }

        .tickets-table tr:last-child td {
          border-bottom: none;
        }

        .ticket-number {
          font-weight: 800;
        }

        .ticket-subject {
          font-weight: 700;
        }

        .ticket-status {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 5px 10px;
          border-radius: 20px;
          font-size: 11px;
          font-weight: 800;
        }

        .status-open {
          background: #fff000;
          color: #000000;
        }

        .status-progress {
          background: #eeeeee;
          color: #000000;
        }

        .status-resolved {
          background: #e8e8e8;
          color: #000000;
        }

        .status-closed {
          background: #000000;
          color: #ffffff;
        }

        .status-default {
          background: #eeeeee;
          color: #000000;
        }

        .view-button {
          border: none;
          outline: none;
          box-shadow: none;
          background: #ffffff;
          color: #000000;
          border-radius: 7px;
          padding: 7px 10px;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-family: inherit;
          font-size: 12px;
          font-weight: 700;
        }

        .view-button:hover {
          background: #000000;
          color: #fff000;
        }

        .view-button:focus,
        .view-button:active {
          border: none;
          outline: none;
          box-shadow: none;
        }

        .empty-tickets {
          padding: 55px 20px;
          text-align: center;
          color: #666666;
        }

        .empty-tickets-icon {
          font-size: 35px;
          color: #000000;
          margin-bottom: 12px;
        }

        .empty-tickets h4 {
          color: #000000;
          margin: 0 0 7px;
          font-weight: 800;
        }

        .empty-tickets p {
          margin: 0 0 18px;
          font-size: 13px;
        }

        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.55);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          z-index: 9999;
        }

        .ticket-modal {
          width: 100%;
          max-width: 620px;
          max-height: 90vh;
          overflow-y: auto;
          background: #ffffff;
          border-radius: 15px;
        }

        .modal-header {
          padding: 20px;
          border-bottom: 1px solid #eeeeee;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .modal-header h3 {
          margin: 0;
          font-size: 19px;
          font-weight: 800;
        }

        .close-button {
          border: none;
          outline: none;
          box-shadow: none;
          background: transparent;
          color: #000000;
          cursor: pointer;
          font-size: 17px;
          padding: 5px;
        }

        .close-button:hover {
          color: #666666;
        }

        .close-button:focus,
        .close-button:active {
          border: none;
          outline: none;
          box-shadow: none;
        }

        .ticket-form {
          padding: 20px;
        }

        .form-group {
          margin-bottom: 17px;
        }

        .form-group label {
          display: block;
          margin-bottom: 7px;
          font-size: 13px;
          font-weight: 800;
        }

        .form-control {
          width: 100%;
          box-sizing: border-box;
          border: 1px solid #d5d5d5;
          border-radius: 8px;
          padding: 11px 12px;
          font-family: inherit;
          font-size: 13px;
          outline: none;
        }

        .form-control:focus {
          border-color: #000000;
        }

        textarea.form-control {
          min-height: 140px;
          resize: vertical;
        }

        .form-actions {
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          margin-top: 20px;
        }

        .form-button {
          border: none;
          outline: none;
          box-shadow: none;
          border-radius: 8px;
          padding: 10px 17px;
          font-family: inherit;
          font-weight: 800;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .form-button.cancel {
          background: #ffffff;
          color: #000000;
        }

        .form-button.cancel:hover {
          background: #eeeeee;
        }

        .form-button.submit {
          background: #fff000;
          color: #000000;
        }

        .form-button.submit:hover {
          background: #000000;
          color: #fff000;
        }

        .form-button:focus,
        .form-button:active {
          border: none;
          outline: none;
          box-shadow: none;
        }

        .form-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .ticket-details {
          padding: 20px;
        }

        .detail-row {
          margin-bottom: 18px;
        }

        .detail-label {
          display: block;
          color: #666666;
          font-size: 11px;
          font-weight: 700;
          margin-bottom: 5px;
          text-transform: uppercase;
        }

        .detail-value {
          font-size: 14px;
          font-weight: 700;
        }

        .ticket-description {
          background: #f8f8f8;
          border-radius: 9px;
          padding: 15px;
          line-height: 1.7;
          font-size: 13px;
          font-weight: 500;
        }

        @media (max-width: 900px) {
          .ticket-summary {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 600px) {
          .support-tickets-tab {
            padding: 16px;
          }

          .ticket-summary {
            grid-template-columns: 1fr;
          }

          .tickets-header {
            align-items: stretch;
          }

          .create-ticket-button {
            justify-content: center;
          }

          .form-actions {
            flex-direction: column;
          }

          .form-button {
            justify-content: center;
          }
        }
      `}</style>

      <div className="tickets-header">
        <div className="tickets-heading">
          <div className="tickets-heading-icon">
            <FaTicketAlt />
          </div>

          <div>
            <h2>Support Tickets</h2>
            <p>Create and manage your support requests.</p>
          </div>
        </div>

        <button
          type="button"
          className="create-ticket-button"
          onClick={() => setShowForm(true)}
        >
          <FaPlus />
          Create New Ticket
        </button>
      </div>

      <div className="ticket-summary">
        <div className="summary-card">
          <div className="summary-card-icon">
            <FaTicketAlt />
          </div>

          <div className="summary-card-value">
            {tickets.length}
          </div>

          <div className="summary-card-label">
            Total Tickets
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-card-icon">
            <FaClock />
          </div>

          <div className="summary-card-value">
            {
              tickets.filter(
                (ticket) =>
                  String(ticket.status).toLowerCase() === "open"
              ).length
            }
          </div>

          <div className="summary-card-label">
            Open
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-card-icon">
            <FaSpinner />
          </div>

          <div className="summary-card-value">
            {
              tickets.filter(
                (ticket) =>
                  String(ticket.status).toLowerCase() ===
                  "in progress"
              ).length
            }
          </div>

          <div className="summary-card-label">
            In Progress
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-card-icon">
            <FaCheckCircle />
          </div>

          <div className="summary-card-value">
            {
              tickets.filter((ticket) =>
                ["resolved", "closed"].includes(
                  String(ticket.status).toLowerCase()
                )
              ).length
            }
          </div>

          <div className="summary-card-label">
            Resolved
          </div>
        </div>
      </div>

      <div className="tickets-section">
        <div className="section-header">
          <h3>My Support Tickets</h3>

          <p>
            Track the status of your submitted support requests.
          </p>
        </div>

        {loading ? (
          <div className="empty-tickets">
            <FaSpinner className="empty-tickets-icon" />
            <p>Loading your support tickets...</p>
          </div>
        ) : tickets.length === 0 ? (
          <div className="empty-tickets">
            <FaTicketAlt className="empty-tickets-icon" />

            <h4>No support tickets yet</h4>

            <p>
              If you need help, create a support ticket and our
              team will assist you.
            </p>

            <button
              type="button"
              className="create-ticket-button"
              onClick={() => setShowForm(true)}
            >
              <FaPlus />
              Create Your First Ticket
            </button>
          </div>
        ) : (
          <div className="tickets-table-wrapper">
            <table className="tickets-table">
              <thead>
                <tr>
                  <th>Ticket</th>
                  <th>Subject</th>
                  <th>Category</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {tickets.map((ticket) => (
                  <tr key={ticket.id}>
                    <td className="ticket-number">
                      {ticket.ticket_number ||
                        `#TK-${ticket.id}`}
                    </td>

                    <td className="ticket-subject">
                      {ticket.subject}
                    </td>

                    <td>
                      {ticket.category || "Other"}
                    </td>

                    <td>
                      {ticket.priority || "Medium"}
                    </td>

                    <td>
                      <span
                        className={`ticket-status ${getStatusClass(
                          ticket.status
                        )}`}
                      >
                        {ticket.status || "Open"}
                      </span>
                    </td>

                    <td>
                      {formatDate(
                        ticket.created_at || ticket.created
                      )}
                    </td>

                    <td>
                      <button
                        type="button"
                        className="view-button"
                        onClick={() =>
                          setSelectedTicket(ticket)
                        }
                      >
                        <FaEye />
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && (
        <div className="modal-overlay">
          <div className="ticket-modal">
            <div className="modal-header">
              <h3>Create Support Ticket</h3>

              <button
                type="button"
                className="close-button"
                onClick={() => {
                  setShowForm(false);
                  resetForm();
                }}
              >
                <FaTimes />
              </button>
            </div>

            <form
              className="ticket-form"
              onSubmit={handleSubmit}
            >
              <div className="form-group">
                <label htmlFor="ticket-subject">
                  Subject
                </label>

                <input
                  id="ticket-subject"
                  type="text"
                  name="subject"
                  className="form-control"
                  placeholder="Briefly describe your issue"
                  value={form.subject}
                  onChange={handleChange}
                  disabled={submitting}
                />
              </div>

              <div className="form-group">
                <label htmlFor="ticket-category">
                  Category
                </label>

                <select
                  id="ticket-category"
                  name="category"
                  className="form-control"
                  value={form.category}
                  onChange={handleChange}
                  disabled={submitting}
                >
                  {categories.map((category) => (
                    <option
                      value={category}
                      key={category}
                    >
                      {category}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="ticket-priority">
                  Priority
                </label>

                <select
                  id="ticket-priority"
                  name="priority"
                  className="form-control"
                  value={form.priority}
                  onChange={handleChange}
                  disabled={submitting}
                >
                  {priorities.map((priority) => (
                    <option
                      value={priority}
                      key={priority}
                    >
                      {priority}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="ticket-description">
                  Description
                </label>

                <textarea
                  id="ticket-description"
                  name="description"
                  className="form-control"
                  placeholder="Describe the problem in detail..."
                  value={form.description}
                  onChange={handleChange}
                  disabled={submitting}
                />
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="form-button cancel"
                  onClick={() => {
                    setShowForm(false);
                    resetForm();
                  }}
                  disabled={submitting}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="form-button submit"
                  disabled={submitting}
                >
                  <FaPaperPlane />

                  {submitting
                    ? "Submitting..."
                    : "Submit Ticket"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {selectedTicket && (
        <div className="modal-overlay">
          <div className="ticket-modal">
            <div className="modal-header">
              <h3>
                {selectedTicket.ticket_number ||
                  `#TK-${selectedTicket.id}`}
              </h3>

              <button
                type="button"
                className="close-button"
                onClick={() => setSelectedTicket(null)}
              >
                <FaTimes />
              </button>
            </div>

            <div className="ticket-details">
              <div className="detail-row">
                <span className="detail-label">
                  Subject
                </span>

                <div className="detail-value">
                  {selectedTicket.subject}
                </div>
              </div>

              <div className="detail-row">
                <span className="detail-label">
                  Category
                </span>

                <div className="detail-value">
                  {selectedTicket.category || "Other"}
                </div>
              </div>

              <div className="detail-row">
                <span className="detail-label">
                  Priority
                </span>

                <div className="detail-value">
                  {selectedTicket.priority || "Medium"}
                </div>
              </div>

              <div className="detail-row">
                <span className="detail-label">
                  Status
                </span>

                <span
                  className={`ticket-status ${getStatusClass(
                    selectedTicket.status
                  )}`}
                >
                  {selectedTicket.status || "Open"}
                </span>
              </div>

              <div className="detail-row">
                <span className="detail-label">
                  Description
                </span>

                <div className="ticket-description">
                  {selectedTicket.description}
                </div>
              </div>

              <div className="detail-row">
                <span className="detail-label">
                  Created
                </span>

                <div className="detail-value">
                  {formatDate(
                    selectedTicket.created_at ||
                      selectedTicket.created
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportTicketsTab;